<template>
  <section class="h-full mx-4" id="validation-logger">
    <BaseTable
      table-id="dcValidationLogs"
      :headers="tableHeaders"
      :list="dcValidationLogs"
      :total-records="dcValidationLogsCount"
      :items-per-page="50"
      hide-multiselect-delete
      is-add-new-hidden
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #filters>
        <div class="grid grid-cols-3 gap-8 mx-10 my-5 pb-8">
          <!-- First column -->
          <div class="space-y-4 mr-4">
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('material.isActive')
              }}</label>
              <BaseTriStateToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                v-model="filterIsActive" />
            </div>
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('material.isActive')
              }}</label>
              <BaseTriStateToggle
                :label="$t('representative.toExport')"
                name="isActive"
                v-model="filterExported" />
            </div>
          </div>
          <!-- Second column -->
          <div class="space-y-4">
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('myDataDocuments.filters.dateFrom')
              }}</label>
              <DateTimePicker
                v-model="filterDateStringFrom"
                start-empty
                show-error-as-tooltip
                clearable
                :show-time-picker="false" />
            </div>
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('myDataDocuments.filters.dateTo')
              }}</label>
              <DateTimePicker
                v-model="filterDateStringTo"
                start-empty
                show-error-as-tooltip
                clearable
                :show-time-picker="false" />
            </div>
          </div>
          <!-- Third column -->
          <div class="space-y-4 z-50">
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('validationLogs.tableName')
              }}</label>
              <BaseMultiSelectDropdown v-model="filterTableNames" :listItems="dropDownTableNames" />
            </div>
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('representative.representative')
              }}</label>
              <BaseMultiSelectDropdown
                v-model="filterRepresentatives"
                :listItems="dropdownRepresentatives"
                :no-results-text="$t('messages.noResultsText')"
                :place-holder-text="$t('common.actions.select')" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';
import { isoDateToFullDate } from '@/utils/date';

import BaseTable from '@/components/common/BaseTable.vue';
import DateTimePicker from '@/components/common/DateTimePicker.vue';
import BaseMultiSelectDropdown from '@/components/common/BaseMultiSelectDropdown.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import { type DCValidationLog, type DCValidationLogsFiltersData } from '@/types/DataCollection';
import {
  getDCValidationLogsWithConditions,
  getValidationLogsDistinctTableNames,
} from '@/services/api/dataCollection';
import type { SelectDropdownItem } from '@/types/Base';

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = ref([
  { value: 'createdDate', text: $t('date.date'), type: 'text', active: true },
  {
    value: 'representativeName',
    text: $t('representative.representative'),
    type: 'text',
    active: true,
  },
  { value: 'remotedCode', text: $t('representative.remotedCode'), type: 'text', active: true },
  { value: 'tableName', text: $t('validationLogs.tableName'), type: 'text', active: true },
  { value: 'exported', text: $t('validationLogs.exported'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: false },
]);

const { getRepresentativesLookup } = useLookupsStore();
const { dropdownRepresentatives } = storeToRefs(useLookupsStore());

const filterDateStringFrom = ref<string | undefined>(undefined);
const filterDateStringTo = ref<string | undefined>(undefined);
const filterIsActive = ref<boolean | undefined>(undefined);
const filterExported = ref<boolean | undefined>(undefined);
const filterRepresentatives = ref<SelectDropdownItem[]>([]);
const filterTableNames = ref<SelectDropdownItem[]>([]);
const dcValidationLogs = ref<DCValidationLog[]>([]);
const dcValidationLogsCount = ref(0);
const tableNames = ref<string[]>([]);
const dropDownTableNames = computed(() =>
  tableNames.value.map((item) => ({
    value: item,
    name: item,
  }))
);

onMounted(async () => {
  await getRepresentativesLookup();
  tableNames.value = await getValidationLogsDistinctTableNames();
  await onRefreshData({});
});
async function onRefreshData(data: DCValidationLogsFiltersData) {
  const sortColumn =
    data.column !== undefined ? tableHeaders.value[data.column]?.value : 'createdDate';
  const sortAscending = data.order === 'asc' || data.order === undefined;

  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    dateFrom: data.dateFrom,
    dateTo: data.dateTo,
    isActive: data.isActive,
    exported: data.exported,
    tableNames: data.tableNames,
    representativeIds: data.representativeIds,
  };

  const res = await getDCValidationLogsWithConditions(params);
  if (res) {
    dcValidationLogs.value = res.data.map((item) => ({
      ...item,
      createdDate: isoDateToFullDate(item.createdDate) || '',
    }));
    dcValidationLogsCount.value = res.count;
  }
}

async function onApplyFilters(data: DCValidationLogsFiltersData) {
  data.isActive = filterIsActive.value;
  data.exported = filterExported.value;
  data.dateFrom = filterDateStringFrom.value || undefined;
  data.dateTo = filterDateStringTo.value || undefined;
  data.representativeIds = filterRepresentatives.value
    .map((item) => item.value)
    .filter((value): value is string => typeof value === 'string');
  data.tableNames = filterTableNames.value
    .map((item) => item.value)
    .filter((value): value is string => typeof value === 'string');

  await onRefreshData(data);
}

async function onResetFilters() {
  filterDateStringFrom.value = undefined;
  filterDateStringTo.value = undefined;
  filterExported.value = undefined;
  filterIsActive.value = undefined;
  filterRepresentatives.value = [];
  filterTableNames.value = [];
  await onRefreshData({});
}
</script>
